import React, { useState, useEffect, useCallback } from 'react';
import { vpnUserAPI, nasAPI } from '../../services/api';
import { toast } from 'react-toastify';
import DataTable from '../../components/DataTable/DataTable';

const DEFAULT_FORM = { username: '', password: '', nasId: '', tunnelIp: '', label: '', isActive: true };

const SYNC_META = {
    applied: { label: 'Applied', className: 'active' },
    pending: { label: 'Pending', className: 'inactive' },
    failed:  { label: 'Failed',  className: 'inactive' },
};

const VpnUsers = () => {
    const [users, setUsers] = useState([]);
    const [nasList, setNasList] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_FORM);
    const [saving, setSaving] = useState(false);

    const [deletingUser, setDeletingUser] = useState(null);

    const [applyingUser, setApplyingUser] = useState(null);
    const [applyAction, setApplyAction] = useState('upsert');
    const [applyPreview, setApplyPreview] = useState(null);
    const [applyLoading, setApplyLoading] = useState(false);
    const [applyResult, setApplyResult] = useState(null);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const res = await vpnUserAPI.getAll();
            setUsers(res.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch VPN users');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
        nasAPI.getAll({ limit: 1000 }).then((res) => setNasList(res.data.data || [])).catch(() => {});
    }, [fetchUsers]);

    function openAdd() {
        setEditingUser(null);
        setFormData(DEFAULT_FORM);
        setShowModal(true);
    }

    function openEdit(user) {
        setEditingUser(user);
        setFormData({
            username: user.username, password: '', nasId: user.nasId?._id || user.nasId || '',
            tunnelIp: user.tunnelIp || '', label: user.label || '', isActive: user.isActive,
        });
        setShowModal(true);
    }

    async function handleSave(e) {
        e.preventDefault();
        if (!editingUser && !formData.password.trim()) {
            toast.warning('Password is required');
            return;
        }
        setSaving(true);
        try {
            const payload = { ...formData, nasId: formData.nasId || null };
            if (!payload.password) delete payload.password;
            if (editingUser) {
                await vpnUserAPI.update(editingUser._id, payload);
                toast.success('VPN user updated');
            } else {
                await vpnUserAPI.create(payload);
                toast.success('VPN user created');
            }
            setShowModal(false);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save VPN user');
        } finally {
            setSaving(false);
        }
    }

    async function confirmDelete(force = false) {
        if (!deletingUser) return;
        try {
            await vpnUserAPI.delete(deletingUser._id, force);
            toast.success('VPN user deleted');
            setDeletingUser(null);
            fetchUsers();
        } catch (error) {
            if (error.response?.status === 409) {
                toast.error(error.response.data.message);
            } else {
                toast.error('Failed to delete VPN user');
            }
        }
    }

    function openApply(user) {
        setApplyingUser(user);
        setApplyAction('upsert');
        setApplyPreview(null);
        setApplyResult(null);
    }

    const loadPreview = useCallback(async () => {
        if (!applyingUser) return;
        setApplyLoading(true);
        setApplyPreview(null);
        try {
            const res = await vpnUserAPI.preview(applyingUser._id, applyAction);
            setApplyPreview(res.data.data);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load preview');
        } finally {
            setApplyLoading(false);
        }
    }, [applyingUser, applyAction]);

    useEffect(() => { if (applyingUser) loadPreview(); }, [applyingUser, applyAction, loadPreview]);

    async function handleApplyNow() {
        if (!applyingUser) return;
        setApplyLoading(true);
        try {
            const res = await vpnUserAPI.apply(applyingUser._id, applyAction);
            setApplyResult({ success: res.data.success, message: res.data.message, steps: res.data.data.steps });
            fetchUsers();
        } catch (error) {
            const data = error.response?.data;
            setApplyResult({ success: false, message: data?.message || 'Apply failed', steps: data?.data?.steps || [] });
        } finally {
            setApplyLoading(false);
        }
    }

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-user-lock"></i>
                            VPN Users
                        </h1>
                        <p className="page-subtitle-modern">L2TP/IPsec credentials for reaching routers behind CGNAT — superadmin only</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary-modern" onClick={openAdd}>
                            <i className="fas fa-plus"></i> Add VPN User
                        </button>
                    </div>
                </div>
            </div>

            <div className="ad-card">
                <div className="ad-card-header">
                    <div className="ad-card-title">
                        <i className="fas fa-shield-alt"></i>
                        <h2>VPN Users</h2>
                    </div>
                </div>
                <div style={{ padding: '0 1.375rem 1.375rem' }}>
                    {loading ? (
                        <div className="loading-modern"><div className="spinner-modern"></div><p>Loading VPN users...</p></div>
                    ) : (
                        <DataTable
                            emptyMessage="No VPN users yet"
                            columns={[
                                { label: 'Username', field: 'username', filter: true },
                                { label: 'NAS / Router', field: 'nasName', render: (row) => row.user.nasId?.name || '—' },
                                { label: 'Tunnel IP', field: 'tunnelIp', render: (row) => row.user.tunnelIp || '—' },
                                {
                                    label: 'Detected IP', field: 'detectedIp', sortable: false,
                                    render: (row) => row.user.observedTunnelIp
                                        ? <span title={new Date(row.user.observedTunnelIp.seenAt).toLocaleString('en-IN')}>{row.user.observedTunnelIp.ip}</span>
                                        : '—',
                                },
                                { label: 'Label', field: 'label' },
                                {
                                    label: 'Active', field: 'activeText',
                                    render: (row) => (
                                        <span className={`status-badge ${row.user.isActive ? 'active' : 'inactive'}`}>
                                            <span className="badge-dot"></span>{row.user.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    ),
                                },
                                {
                                    label: 'Sync Status', field: 'syncStatus',
                                    filter: { options: ['applied', 'pending', 'failed'] },
                                    render: (row) => {
                                        const meta = SYNC_META[row.user.syncStatus] || SYNC_META.pending;
                                        return (
                                            <span className={`status-badge ${meta.className}`}>
                                                <span className="badge-dot"></span>{meta.label}
                                            </span>
                                        );
                                    },
                                },
                                {
                                    label: 'Last Applied', field: 'lastAppliedText', sortable: false,
                                    render: (row) => row.user.lastAppliedAt ? new Date(row.user.lastAppliedAt).toLocaleString('en-IN') : '—',
                                },
                                {
                                    label: 'Action', field: 'action', sortable: false, searchValue: () => '',
                                    render: (row) => (
                                        <div className="table-row-actions">
                                            <button className="action-btn-icon" onClick={() => openApply(row.user)} title="Apply to Server" style={{ color: '#0891b2', background: '#ecfeff' }}>
                                                <i className="fas fa-cloud-upload-alt"></i>
                                            </button>
                                            <button className="action-btn-icon edit" onClick={() => openEdit(row.user)} title="Edit"><i className="fas fa-edit"></i></button>
                                            <button className="action-btn-icon delete" onClick={() => setDeletingUser(row.user)} title="Delete"><i className="fas fa-trash"></i></button>
                                        </div>
                                    ),
                                },
                            ]}
                            rows={users.map((user) => ({
                                id: user._id, user,
                                username: user.username,
                                nasName: user.nasId?.name || '',
                                tunnelIp: user.tunnelIp || '',
                                label: user.label || '',
                                activeText: user.isActive ? 'active' : 'inactive',
                                syncStatus: user.syncStatus,
                                lastAppliedText: user.lastAppliedAt || '',
                            }))}
                        />
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>{editingUser ? 'Edit VPN User' : 'Add VPN User'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    <div className="form-group-modern">
                                        <label>Username</label>
                                        <input
                                            type="text" value={formData.username}
                                            onChange={(e) => setFormData((f) => ({ ...f, username: e.target.value }))}
                                            required disabled={editingUser?.syncStatus === 'applied'}
                                        />
                                        {editingUser?.syncStatus === 'applied' && (
                                            <p className="input-hint">Already applied to the server — remove it (Apply → Remove) before renaming.</p>
                                        )}
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Password</label>
                                        <input
                                            type="password" value={formData.password}
                                            onChange={(e) => setFormData((f) => ({ ...f, password: e.target.value }))}
                                            placeholder={editingUser ? 'Leave blank to keep existing' : ''}
                                            required={!editingUser}
                                        />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>NAS / Router <span className="field-opt">(optional)</span></label>
                                        <select value={formData.nasId} onChange={(e) => setFormData((f) => ({ ...f, nasId: e.target.value }))}>
                                            <option value="">Not linked</option>
                                            {nasList.map((n) => <option key={n._id} value={n._id}>{n.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Tunnel IP <span className="field-opt">(optional)</span></label>
                                        <input
                                            type="text" placeholder="e.g. 192.168.42.50 — leave blank for pool-assigned"
                                            value={formData.tunnelIp}
                                            onChange={(e) => setFormData((f) => ({ ...f, tunnelIp: e.target.value }))}
                                        />
                                        <p className="input-hint">A static IP so this router gets the same tunnel address every reconnect — needed to use it as a stable Router API Host.</p>
                                        {editingUser?.observedTunnelIp && editingUser.observedTunnelIp.ip !== formData.tunnelIp && (
                                            <p className="input-hint" style={{ color: '#0891b2', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                <i className="fas fa-satellite-dish"></i>
                                                Last seen connecting with <strong>{editingUser.observedTunnelIp.ip}</strong> ({new Date(editingUser.observedTunnelIp.seenAt).toLocaleString('en-IN')})
                                                <button
                                                    type="button"
                                                    className="btn-secondary-modern btn-sm"
                                                    onClick={() => setFormData((f) => ({ ...f, tunnelIp: editingUser.observedTunnelIp.ip }))}
                                                >
                                                    Use this
                                                </button>
                                            </p>
                                        )}
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label>Label <span className="field-opt">(optional)</span></label>
                                        <input
                                            type="text" placeholder="e.g. Oakwood Pune - hEX router"
                                            value={formData.label}
                                            onChange={(e) => setFormData((f) => ({ ...f, label: e.target.value }))}
                                        />
                                    </div>
                                    <div className="form-group-modern">
                                        <label className="checkbox-wrapper">
                                            <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData((f) => ({ ...f, isActive: e.target.checked }))} />
                                            <span>Active</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deletingUser && (
                <div className="modal-overlay-modern" onClick={() => setDeletingUser(null)}>
                    <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern danger">
                            <h2>Delete VPN User</h2>
                            <button className="modal-close" onClick={() => setDeletingUser(null)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="warning-box" style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '1rem' }}>
                                <p>Are you sure you want to delete <strong>{deletingUser.username}</strong>?</p>
                                {deletingUser.syncStatus === 'applied' && (
                                    <p style={{ color: '#b91c1c', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                                        <i className="fas fa-exclamation-triangle"></i> This user is applied on the server. Deleting the record here will
                                        NOT remove it from chap-secrets — use Apply → Remove first, or force-delete the record only.
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setDeletingUser(null)}>Cancel</button>
                            {deletingUser.syncStatus === 'applied' ? (
                                <button className="btn-danger-modern" onClick={() => confirmDelete(true)}>
                                    <i className="fas fa-trash"></i> Force Delete Record Only
                                </button>
                            ) : (
                                <button className="btn-danger-modern" onClick={() => confirmDelete(false)}>
                                    <i className="fas fa-trash"></i> Delete
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Apply Modal */}
            {applyingUser && (
                <div className="modal-overlay-modern" onClick={() => setApplyingUser(null)}>
                    <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>Apply to Server</h2>
                                <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 2 }}>{applyingUser.username}</p>
                            </div>
                            <button className="modal-close" onClick={() => setApplyingUser(null)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="form-group-modern">
                                <label>Action</label>
                                <select value={applyAction} onChange={(e) => { setApplyAction(e.target.value); setApplyResult(null); }} disabled={applyLoading}>
                                    <option value="upsert">Add / Update on server</option>
                                    <option value="remove">Remove from server</option>
                                </select>
                            </div>

                            {applyLoading && !applyResult && (
                                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading preview...</p></div>
                            )}

                            {applyPreview && !applyResult && (
                                <div>
                                    <p className="input-hint" style={{ marginBottom: 8 }}>
                                        {applyPreview.changed ? 'This is exactly what will change in chap-secrets — nothing has been written yet.' : 'No change needed — the server already matches this.'}
                                    </p>
                                    {applyPreview.changed && (
                                        <pre style={{
                                            background: '#0f172a', color: '#e2e8f0', borderRadius: '10px', padding: '1rem',
                                            fontSize: '0.72rem', lineHeight: 1.6, overflowX: 'auto', maxHeight: 260,
                                            fontFamily: '"Courier New", monospace', whiteSpace: 'pre',
                                        }}>{applyPreview.after}</pre>
                                    )}
                                </div>
                            )}

                            {applyResult && (
                                <div>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                                        background: applyResult.success ? '#f0fdf4' : '#fff7ed',
                                        border: `1px solid ${applyResult.success ? '#bbf7d0' : '#fed7aa'}`,
                                        borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1rem',
                                    }}>
                                        <i className={`fas ${applyResult.success ? 'fa-check-circle' : 'fa-exclamation-circle'}`}
                                            style={{ fontSize: '1.4rem', color: applyResult.success ? '#10b981' : '#f59e0b' }}></i>
                                        <div style={{ fontWeight: 700, color: applyResult.success ? '#065f46' : '#92400e' }}>{applyResult.message}</div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                        {applyResult.steps?.map((entry, i) => {
                                            const isOk = entry.status === 'ok';
                                            const isSkipped = entry.status === 'skipped';
                                            return (
                                                <div key={i} style={{
                                                    display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
                                                    background: isOk ? '#f9fafb' : isSkipped ? '#fafaf5' : '#fff1f2',
                                                    border: `1px solid ${isOk ? '#e5e7eb' : isSkipped ? '#d4d4aa' : '#fca5a5'}`,
                                                    borderRadius: '7px', padding: '0.45rem 0.75rem', fontSize: '0.8rem',
                                                }}>
                                                    <i className={`fas ${isOk ? 'fa-check' : isSkipped ? 'fa-minus' : 'fa-times'}`}
                                                        style={{ color: isOk ? '#10b981' : isSkipped ? '#a16207' : '#ef4444', marginTop: 2, flexShrink: 0 }}></i>
                                                    <div>
                                                        <span style={{ fontWeight: 600, color: '#374151' }}>{entry.step}</span>
                                                        {entry.detail && <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: 1 }}>{entry.detail}</div>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setApplyingUser(null)}>Close</button>
                            {!applyResult && (
                                <button className="btn-primary-modern" onClick={handleApplyNow} disabled={applyLoading || !applyPreview?.changed}>
                                    <i className="fas fa-cloud-upload-alt"></i> Apply to Server
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VpnUsers;
